import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { LEGAL } from "@/data/legal";
import { SmartLink } from "@/components/SmartLink";
import { Contours } from "@/components/Contours";
import { usePageMeta } from "@/hooks/usePageMeta";

export const LegalPage = ({ id }) => {
  const { t, lang } = useLanguage();
  const entry = LEGAL[id];
  // Entries are either { title, html } (German only) or { de: {...}, en: {...} }.
  const localized = entry && (entry.de || entry.en);
  const page = localized ? entry[lang] || entry.de : entry;
  const showLangNote = lang !== "de" && !(localized && entry[lang]);

  usePageMeta({
    title: page ? `${page.title} – HuntersMapp` : "",
    description: t.meta.legalDescription,
    path: `/${id}`,
    lang,
  });

  if (!page) return null;

  return (
    <article className="relative">
      <div className="absolute inset-x-0 top-0 h-[420px] overflow-hidden" aria-hidden="true">
        <Contours rings={12} step={60} seed={id.length} cx={1100} cy={200} opacity={0.1} />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[var(--bg)]" />
      </div>
      <div className="relative mx-auto max-w-3xl px-5 pb-24 pt-32 sm:px-6 md:pt-40">
        <SmartLink href="/" className="inline-flex items-center gap-2 text-sm text-[var(--ink-2)] transition-colors hover:text-white" data-testid="legal-back">
          <ArrowLeft className="h-4 w-4" />
          {t.legal.back}
        </SmartLink>
        <p className="eyebrow mt-10">{t.legal.eyebrow}</p>
        <h1 className="font-display mt-4 text-4xl leading-[1.05] sm:text-5xl">{page.title}</h1>
        {page.updated && (
          <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-[var(--ink-3)]">
            {t.legal.updated}: {page.updated}
          </p>
        )}
        {showLangNote && t.legal.langNote && (
          <p className="mt-6 rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-sm text-[var(--ink-2)]">
            {t.legal.langNote}
          </p>
        )}
        {/* Content is the client's own legal text, sanitized at import time to a fixed tag whitelist. */}
        <div
          className="prose-legal mt-12"
          data-testid={`legal-content-${id}`}
          dangerouslySetInnerHTML={{ __html: page.html }}
        />
      </div>
    </article>
  );
};
